import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  patchEier, patchNeste, patchInfo,
  SPEAKER_STAGES, PARTNER_STAGES, KONTAKT_STATUSER, PRI_VALG,
  ROUTE_TYPES, FIRMA_KATEGORIER, PARTNER_ROLLER, PARTNERSKAP_KATEGORIER, SPEAKER_ROLLER, TARGET_TYPER,
} from "./blokkData";

const TITLER = {
  stage: "Status", pri: "Priority", eier: "Owner", katFirma: "Company category",
  pkat: "Partnership category", targetType: "Target type",
  rolle: "Role", ask: "Desired outcome", route: "Contact route", neste: "Next move", frist: "Due",
  geo: "Geo", land: "Country",
};

export default function CelleEditor({ edit, medlemmer, onLukk, refresh }) {
  const { rad, felt } = edit;
  const [tekst, setTekst] = useState(() => (felt === "ask" ? rad.ask || "" : felt === "neste" ? rad.nesteSteg || "" : felt === "rolle" ? rad.rolleType || "" : felt === "land" ? rad.land || "" : rad.route || ""));
  const [dato, setDato] = useState(rad.frist || "");
  const [ruteType, setRuteType] = useState(rad.routeType || "");
  const [jobber, setJobber] = useState(false);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk]);

  const lagre = async (fn, melding = "Lagret") => {
    if (jobber) return;
    setJobber(true);
    try {
      await fn();
      toast.success(melding);
      refresh();
      onLukk();
    } catch {
      toast.error("Kunne ikke lagre");
      setJobber(false);
    }
  };

  const Valg = ({ aktiv, onVelg, children, testid }) => (
    <button type="button" className={`dok-celle-valg ${aktiv ? "aktiv" : ""}`} onClick={onVelg} disabled={jobber} data-testid={testid}>
      <span className="flex-1 min-w-0 text-left truncate">{children}</span>
      {aktiv && <Check size={11} />}
    </button>
  );

  let innhold = null;
  if (felt === "stage") {
    const stages = rad.type === "kontakt" ? KONTAKT_STATUSER : rad.type === "partner" ? PARTNER_STAGES : SPEAKER_STAGES;
    innhold = stages.map(([k, l]) => (
      <Valg key={k} aktiv={rad.stage === k} testid={`dok-celle-stage-${k}`}
        onVelg={() => lagre(() => (rad.type === "kontakt"
          ? api.patch(`/admin/os/kontakter/${rad.id}`, { status: k })
          : patchInfo(rad, { stage: k })), "Status oppdatert")}>{l}</Valg>
    ));
  } else if (felt === "pri") {
    innhold = (<>
      {PRI_VALG.map(([k, l]) => (
        <Valg key={k} aktiv={rad.pri === k} testid={`dok-celle-pri-${k}`}
          onVelg={() => lagre(() => patchInfo(rad, { pri: k }), "Prioritet oppdatert")}>{l}</Valg>
      ))}
      <Valg aktiv={!rad.pri} testid="dok-celle-pri-ingen"
        onVelg={() => lagre(() => patchInfo(rad, { pri: "" }), "Prioritet fjernet")}>Ingen</Valg>
    </>);
  } else if (felt === "eier") {
    innhold = (<>
      {medlemmer.map((m) => (
        <Valg key={m.id} aktiv={rad.eierId === m.id} testid={`dok-celle-eier-${m.id}`}
          onVelg={() => lagre(() => patchEier(rad, m.id), "Eier oppdatert")}>
          <i className="dok-celle-init" style={{ background: m.color || "#8B5CF6" }}>{(m.name || "?")[0]}</i>{m.name}
        </Valg>
      ))}
      <Valg aktiv={!rad.eierId} testid="dok-celle-eier-ingen"
        onVelg={() => lagre(() => patchEier(rad, null), "Eier fjernet")}>Unassigned</Valg>
    </>);
  } else if (felt === "katFirma") {
    innhold = (<>
      {FIRMA_KATEGORIER.map((k) => (
        <Valg key={k} aktiv={rad.kategoriFirma === k} testid={`dok-celle-kat-${k}`}
          onVelg={() => lagre(() => patchInfo(rad, { companyCategory: k }), "Kategori oppdatert")}>{k}</Valg>
      ))}
      <Valg aktiv={!rad.kategoriFirma} testid="dok-celle-kat-ingen"
        onVelg={() => lagre(() => patchInfo(rad, { companyCategory: "" }), "Kategori fjernet")}>—</Valg>
    </>);
  } else if (felt === "pkat") {
    innhold = (<>
      {PARTNERSKAP_KATEGORIER.map((k) => (
        <Valg key={k} aktiv={rad.partnerKategori === k} testid={`dok-celle-pkat-${k}`}
          onVelg={() => lagre(() => patchInfo(rad, { partnershipCategory: k }), "Partnership category oppdatert")}>{k}</Valg>
      ))}
      <Valg aktiv={!rad.partnerKategori} testid="dok-celle-pkat-ingen"
        onVelg={() => lagre(() => patchInfo(rad, { partnershipCategory: "" }), "Partnership category fjernet")}>—</Valg>
    </>);
  } else if (felt === "targetType") {
    innhold = TARGET_TYPER.map(([k, l]) => (
      <Valg key={k} aktiv={(rad.targetType || "person") === k} testid={`dok-celle-type-${k}`}
        onVelg={() => lagre(() => patchInfo(rad, { targetType: k }), "Target type oppdatert")}>{l}</Valg>
    ));
  } else if (felt === "rolle" && rad.type === "speaker") {
    const roller = SPEAKER_ROLLER.includes(rad.rolleType) || !rad.rolleType
      ? SPEAKER_ROLLER : [rad.rolleType, ...SPEAKER_ROLLER];
    innhold = (<>
      {roller.map((k) => (
        <Valg key={k} aktiv={rad.rolleType === k} testid={`dok-celle-srolle-${k}`}
          onVelg={() => lagre(() => patchInfo(rad, { roleLabel: k }), "Rolle oppdatert")}>{k}</Valg>
      ))}
      <Valg aktiv={!rad.rolleType} testid="dok-celle-srolle-ingen"
        onVelg={() => lagre(() => patchInfo(rad, { roleLabel: "" }), "Rolle fjernet")}>—</Valg>
    </>);
  } else if (felt === "rolle" && rad.type === "partner") {
    innhold = (<>
      {PARTNER_ROLLER.map((k) => (
        <Valg key={k} aktiv={rad.rolleType === k} testid={`dok-celle-rolle-${k}`}
          onVelg={() => lagre(() => patchInfo(rad, { packageName: k }), "Partnerrolle oppdatert")}>{k}</Valg>
      ))}
      <Valg aktiv={!rad.rolleType} testid="dok-celle-rolle-ingen"
        onVelg={() => lagre(() => patchInfo(rad, { packageName: "" }), "Partnerrolle fjernet")}>—</Valg>
    </>);
  } else if (felt === "geo") {
    innhold = (<>
      {[["norge", "Norge"], ["int", "Internasjonalt"]].map(([k, l]) => (
        <Valg key={k} aktiv={rad.geo === k} testid={`dok-celle-geo-${k}`}
          onVelg={() => lagre(() => patchInfo(rad, { geo: k }), "Geo oppdatert")}>{l}</Valg>
      ))}
      <Valg aktiv={!rad.geo} testid="dok-celle-geo-ingen"
        onVelg={() => lagre(() => patchInfo(rad, { geo: "" }), "Geo fjernet")}>—</Valg>
    </>);
  } else if (felt === "ask" || felt === "neste" || felt === "land") {
    const send = (e) => {
      e.preventDefault();
      const v = tekst.trim();
      if (felt === "neste") lagre(() => patchNeste(rad, v, rad.frist || ""), "Next move lagret");
      else if (felt === "ask") lagre(() => patchInfo(rad, { ask: v }), "Desired outcome lagret");
      else lagre(() => patchInfo(rad, { country: v }), "Country lagret");
    };
    innhold = (
      <form onSubmit={send} className="dok-celle-form">
        <input autoFocus value={tekst} onChange={(e) => setTekst(e.target.value)}
          placeholder={felt === "neste" ? "Hva er neste konkrete trekk?" : felt === "ask" ? "Ønsket rolle / outcome" : "F.eks. Norge, USA, UK"}
          className="dok-celle-input" data-testid="dok-celle-tekst" />
        <button type="submit" className="dok-celle-lagre" disabled={jobber} data-testid="dok-celle-lagre">{jobber ? "…" : "Lagre"}</button>
      </form>
    );
  } else if (felt === "route") {
    const send = (e) => {
      e.preventDefault();
      lagre(() => patchInfo(rad, { routeType: ruteType, route: tekst.trim() }), "Contact route lagret");
    };
    innhold = (
      <form onSubmit={send} className="dok-celle-form kolonne">
        <select value={ruteType} onChange={(e) => setRuteType(e.target.value)} className="dok-celle-input" data-testid="dok-celle-routetype">
          {ROUTE_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <div className="flex gap-1.5">
          <input autoFocus value={tekst} onChange={(e) => setTekst(e.target.value)} placeholder="Via hvem / notat"
            className="dok-celle-input flex-1" data-testid="dok-celle-routenote" />
          <button type="submit" className="dok-celle-lagre" disabled={jobber} data-testid="dok-celle-lagre">{jobber ? "…" : "Lagre"}</button>
        </div>
      </form>
    );
  } else if (felt === "frist") {
    innhold = (
      <div className="dok-celle-form kolonne">
        <input type="date" value={dato} onChange={(e) => setDato(e.target.value)} className="dok-celle-input" data-testid="dok-celle-dato" />
        <div className="flex gap-1.5">
          <button type="button" className="dok-celle-lagre" disabled={jobber} data-testid="dok-celle-lagre"
            onClick={() => lagre(() => patchNeste(rad, rad.nesteSteg || "", dato), "Frist lagret")}>{jobber ? "…" : "Lagre"}</button>
          {rad.frist && (
            <button type="button" className="dok-celle-fjern" disabled={jobber} data-testid="dok-celle-fjern-dato"
              onClick={() => lagre(() => patchNeste(rad, rad.nesteSteg || "", ""), "Frist fjernet")}>Fjern frist</button>
          )}
        </div>
      </div>
    );
  }

  const bred = ["ask", "neste", "route", "land"].includes(felt) ? 300 : 232;
  const x = Math.max(10, Math.min(edit.x, window.innerWidth - bred - 16));
  const flip = window.innerHeight - edit.y < 340;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[80]" onClick={onLukk} data-testid="dok-celle-backdrop" />
      <div className="dok-celle-editor" style={{ left: x, width: bred, ...(flip ? { bottom: window.innerHeight - edit.y + 30 } : { top: edit.y }) }} data-testid="dok-celle-editor">
        <p className="dok-celle-tittel">{TITLER[felt]} · {rad.navn}</p>
        <div className="dok-celle-innhold">{innhold}</div>
      </div>
    </>,
    document.body,
  );
}
